/**
 * Levels — the Design-tab surface for procgen level artifacts (Epic C), sibling to
 * Screens. Increment 1: the `useLevels` CRUD hook + a JSON-backed detail panel that runs
 * the real `@volley/crucible-levels` validator live. The Three.js viewport editor + AI
 * Generate land in later increments.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LevelEntry } from '@volley/crucible-ipc-types';
import {
  registerBuiltinKinds,
  getKind,
  ENTITY_ROLES,
  type LevelFinding,
  type ThreeCsProfile,
  type SpatialLevel,
  type TopdownLevel,
  type DataTableLevel,
  type BoardGridLevel,
  type EntityRole,
} from '@volley/crucible-levels';
import type { DesignProject } from '@volley/crucible-ipc-types';
import {
  listLevels,
  writeLevel,
  deleteLevel,
  writePrevisSkin,
  readSection,
  judgeLevel,
  reviseLevel,
} from './anvil-api';
import { LevelViewport, type LevelSelection } from './LevelViewport';
import { TopdownViewport, type TopdownSelection } from './TopdownViewport';
import { DataTableEditor } from './DataTableEditor';
import { BoardGridEditor } from './BoardGridEditor';
import { deriveBiomePalette, type PrevisSkin, type PrevisImages } from './previs-skin';
import { humanizeFinding } from './finding-humanize';
import { fixLevelWithAI, reimagineLevelWithAI } from './level-generate';
import { readContentPalette, type PaletteItem } from './level-wizard';
import { TagsField } from './TagsField';
import { biomeSites, usageCount, type UsageIndex } from './lib/usage-index';
import { useCrucibleClient } from '../../transport/context.js';

// Register built-in kinds once (tolerate HMR re-runs / double registration).
let kindsReady = false;
function ensureKinds(): void {
  if (kindsReady) return;
  try {
    registerBuiltinKinds();
  } catch {
    // already registered in this module instance
  }
  kindsReady = true;
}

// Defaults + kind palette live in level-defaults.ts (shared with level-generate
// without an import cycle); re-exported here for existing importers.
export {
  DEFAULT_THREE_CS,
  DEFAULT_TOPDOWN_THREE_CS,
  DEFAULT_DATA_THREE_CS,
} from './level-defaults';
import {
  threeCsForKind,
  kindForArchetype,
  isKindChoice,
  kindShortLabel,
  type LevelKindChoice,
} from './level-defaults';

function freshId(): string {
  return `level_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

/** A minimal, schema-valid starter spatial-2d track. */
function starterSpatialData(): unknown {
  return {
    archetype: 'autorunner-2d',
    targetSeconds: 60,
    vertices: [
      { id: 'start', t: 0, elevation: 0 },
      { id: 'gap-end', t: 0.5, elevation: 0 },
      { id: 'finish', t: 1, elevation: 0 },
    ],
    segments: [
      { fromId: 'start', toId: 'gap-end', kind: 'platform', span: 10 },
      { fromId: 'gap-end', toId: 'finish', kind: 'gap', span: 4 },
    ],
  };
}

/** A minimal, schema-valid starter topdown-2d floor plan. */
function starterTopdownData(): unknown {
  return {
    archetype: 'topdown-2d',
    targetSeconds: 90,
    rooms: [
      { id: 'entry', x: 0, y: 0, w: 10, h: 8, kind: 'start' },
      { id: 'hall', x: 12, y: 0, w: 12, h: 8, kind: 'normal' },
      { id: 'shrine', x: 26, y: 0, w: 10, h: 8, kind: 'goal' },
    ],
    corridors: [
      { fromId: 'entry', toId: 'hall', width: 2 },
      { fromId: 'hall', toId: 'shrine', width: 2 },
    ],
    keys: [],
  };
}

/** A minimal, schema-valid starter data-table (loot-table shaped; edit freely). */
function starterDataTableData(): unknown {
  return {
    weightMode: 'relative',
    rows: [
      { id: 'common-drop', weight: 3 },
      { id: 'rare-drop', weight: 1 },
    ],
  };
}

/** A minimal starter board (empty 8×8 with a placeholder palette; paint away). */
function starterBoardGridData(): unknown {
  return { width: 8, height: 8, palette: ['tile-a', 'tile-b'], cells: [] };
}

const SEGMENT_PALETTE = ['platform', 'gap', 'fence', 'reversal', 'collapse'] as const;

// Plain-language help for the track pieces — the jargon the newcomer eval flagged
// (esp. "fence", which reads like a wall but is a jump-over obstacle).
const SEGMENT_HELP: Record<string, string> = {
  platform: 'Platform — solid ground the runner runs across.',
  gap: 'Gap — a pit; the runner falls in unless they jump it.',
  fence: 'Fence — a jump-over obstacle standing ON the ground (hurdle, not a wall).',
  reversal: 'Reversal — a timed zone that flips the run direction.',
  collapse: 'Collapse — floor that crumbles behind you; keep moving to outrun it.',
};

function defaultSegment(kind: string, fromId: string, toId: string): Record<string, unknown> {
  switch (kind) {
    case 'fence':
      return { fromId, toId, kind, span: 1, obstacleHeight: 1.5, obstacleWidth: 0.5 };
    case 'reversal':
      return { fromId, toId, kind, span: 6, clearWindowS: 5 };
    case 'collapse':
      return { fromId, toId, kind, span: 8, collapseSpeed: 3 };
    case 'gap':
      return { fromId, toId, kind, span: 3 };
    default:
      return { fromId, toId, kind: 'platform', span: 6 };
  }
}

interface TrackVertex {
  id: string;
  t: number;
  elevation: number;
}
interface TrackData {
  vertices: TrackVertex[];
  segments: Record<string, unknown>[];
}

/**
 * Pure track-topology edit: insert a new segment (and the vertex it needs) BEFORE or AFTER
 * segment `at`, keeping the vertex chain contiguous and re-spreading `t` evenly. Returns a
 * fresh {vertices, segments} plus the index the new segment landed at (for selection), or
 * null when there's nothing to anchor to. Extracted from the editor so the reorder topology
 * is unit-testable.
 *
 * - after  (default / append): new vertex after the anchor (segment's end), new piece at at+1.
 * - before (add-left of selection): a new vertex splits off the selected segment's start; the
 *   new piece runs into it and the selected segment now starts from the new vertex; piece at `at`.
 */
export function spliceTrackSegment(
  data: TrackData,
  at: number,
  side: 'before' | 'after',
  makeSeg: (fromId: string, toId: string) => Record<string, unknown>,
): { vertices: TrackVertex[]; segments: Record<string, unknown>[]; index: number } | null {
  const vertices = data.vertices.map((v) => ({ ...v }));
  const segments = data.segments.map((s) => ({ ...s }));
  if (vertices.length === 0) return null;
  const freshId = (): string => {
    let n = vertices.length;
    let id = `v${n}`;
    while (vertices.some((v) => v.id === id)) {
      n += 1;
      id = `v${n}`;
    }
    return id;
  };
  const renormalizeT = (): void =>
    vertices.forEach((v, i) => {
      v.t = vertices.length === 1 ? 0 : i / (vertices.length - 1);
    });

  if (side === 'before' && segments[at]) {
    const anchorId = segments[at].fromId as string;
    const j = vertices.findIndex((v) => v.id === anchorId);
    if (j === -1) return null;
    const newId = freshId();
    vertices.splice(j + 1, 0, { id: newId, t: 0, elevation: vertices[j].elevation ?? 0 });
    renormalizeT();
    segments[at].fromId = newId;
    segments.splice(at, 0, makeSeg(anchorId, newId));
    return { vertices, segments, index: at };
  }

  const anchorId = (segments[at]?.toId as string | undefined) ?? vertices[vertices.length - 1].id;
  const j = vertices.findIndex((v) => v.id === anchorId);
  if (j === -1) return null;
  const newId = freshId();
  vertices.splice(j + 1, 0, { id: newId, t: 0, elevation: vertices[j].elevation ?? 0 });
  renormalizeT();
  const next = segments[at + 1];
  if (next && next.fromId === anchorId) next.fromId = newId;
  segments.splice(at + 1, 0, makeSeg(anchorId, newId));
  return { vertices, segments, index: at + 1 };
}

function blankLevel(threeCs: ThreeCsProfile, kindOverride?: LevelKindChoice): LevelEntry {
  const kind = kindOverride ?? kindForArchetype(threeCs.archetype);
  return {
    id: freshId(),
    name: 'New level',
    kind,
    data:
      kind === 'topdown-2d'
        ? starterTopdownData()
        : kind === 'data-table'
          ? starterDataTableData()
          : kind === 'board-grid'
            ? starterBoardGridData()
            : starterSpatialData(),
    createdAt: '',
    updatedAt: '',
  };
}

export interface LevelsState {
  levels: LevelEntry[];
  loaded: boolean;
  save: (level: LevelEntry) => Promise<LevelEntry>;
  remove: (id: string) => Promise<void>;
  /** Blank starter — the game's archetype kind by default, or an explicit kind
   *  from the palette (design §17: kind is a per-level choice). */
  create: (threeCs: ThreeCsProfile, kind?: LevelKindChoice) => Promise<LevelEntry>;
}

export function useLevels(gameId: string): LevelsState {
  const client = useCrucibleClient();
  const [levels, setLevels] = useState<LevelEntry[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    listLevels(client, gameId)
      .then((list) => {
        if (!cancelled) {
          setLevels(list);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, client]);

  const save = useCallback(
    async (level: LevelEntry): Promise<LevelEntry> => {
      const written = await writeLevel(client, gameId, level);
      setLevels((prev) =>
        prev.some((l) => l.id === written.id)
          ? prev.map((l) => (l.id === written.id ? written : l))
          : [...prev, written],
      );
      return written;
    },
    [gameId, client],
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      await deleteLevel(client, gameId, id);
      setLevels((prev) => prev.filter((l) => l.id !== id));
    },
    [gameId, client],
  );

  const create = useCallback(
    (threeCs: ThreeCsProfile, kind?: LevelKindChoice): Promise<LevelEntry> =>
      save(blankLevel(threeCs, kind)),
    [save],
  );

  return { levels, loaded, save, remove, create };
}

interface Validation {
  ok: boolean;
  parseError: string | null;
  findings: LevelFinding[];
  /** The schema-valid parsed artifact (present only when the schema check passed). */
  parsed?: unknown;
}

function validate(
  kind: string,
  dataText: string,
  threeCs: ThreeCsProfile,
  palette?: ReadonlySet<string>,
  levelIndex?: number,
  tags?: readonly string[],
): Validation {
  ensureKinds();
  let parsed: unknown;
  try {
    parsed = JSON.parse(dataText);
  } catch (e) {
    return { ok: false, parseError: e instanceof Error ? e.message : String(e), findings: [] };
  }
  try {
    const k = getKind(kind);
    const schemaCheck = k.schema.safeParse(parsed);
    if (!schemaCheck.success) {
      return {
        ok: false,
        parseError: null,
        findings: schemaCheck.error.issues.map((i) => ({
          severity: 'error' as const,
          code: 'schema',
          message: `${i.path.join('.') || '(root)'}: ${i.message}`,
        })),
      };
    }
    const findings = k.validate(schemaCheck.data, {
      threeCs: k.consumesThreeCs ? threeCs : undefined,
      palette,
      levelIndex,
      tags,
    });
    return {
      ok: findings.every((f) => f.severity !== 'error'),
      parseError: null,
      findings,
      parsed: schemaCheck.data,
    };
  } catch (e) {
    return { ok: false, parseError: e instanceof Error ? e.message : String(e), findings: [] };
  }
}

const SEVERITY_CLASS: Record<string, string> = {
  error: 'text-red-400',
  warn: 'text-amber-300',
  info: 'text-crucible-muted',
};

export function LevelDetail({
  gameId,
  level,
  judgeEnabled = false,
  threeCs,
  project,
  allLevels,
  usageIndex,
  progression,
  slot,
  onChange,
  onDelete,
  onTagClick,
  tagSuggestions,
  palette,
  previsSkin,
  previsImages,
  onPrevisSkinChange,
}: {
  gameId: string;
  level: LevelEntry;
  /** Expert design-judge gate (CRUCIBLE_LEVEL_DESIGN_PRO). Hides "Review design" when off. */
  judgeEnabled?: boolean;
  threeCs: ThreeCsProfile;
  /** For Reimagine-with-context (design dump + coherent slotting). */
  project: DesignProject;
  allLevels?: LevelEntry[];
  /** Usage graph (design §21) — powers the "used in N levels" count in the biome picker. */
  usageIndex?: UsageIndex;
  progression?: unknown;
  slot?: { label: string; index: number; total: number };
  onChange: (level: LevelEntry) => void;
  onDelete: () => void;
  /** Clicking a tag chip (opens the tag-filtered search). */
  onTagClick?: (tag: string) => void;
  /** Known tags across the design (most-used first) — TagsField autocomplete. */
  tagSuggestions?: string[];
  /** Design-item names — the ref-integrity palette (orphan-ref warnings, design §18). */
  palette?: ReadonlySet<string>;
  /** Previs palette (design §19) — viewports render in the game's look when set. */
  previsSkin?: PrevisSkin | null;
  /** Previs Phase 2 — decoded image layers from the built game. */
  previsImages?: PrevisImages | null;
  /** Lift a skin edit (a "new biome" adds to the skin's biome registry, design §20) to the
   *  owner so the picker + previs see it. Omitted → new biomes still apply to the level but
   *  won't appear in the registry until reload. */
  onPrevisSkinChange?: (skin: PrevisSkin) => void;
}): React.ReactElement {
  const client = useCrucibleClient();
  const [name, setName] = useState(level.name);
  const [tags, setTags] = useState<string[]>(level.tags ?? []);
  const [dataText, setDataText] = useState(() => JSON.stringify(level.data, null, 2));
  const [sel, setSel] = useState<LevelSelection | null>(null);
  const [tdSel, setTdSel] = useState<TopdownSelection | null>(null);
  // "+ object" picker (place a real Content item, never a placeholder).
  const [objPickerOpen, setObjPickerOpen] = useState(false);
  const [objFilter, setObjFilter] = useState('');
  const [segPickerOpen, setSegPickerOpen] = useState(false);
  // Where a new track piece lands relative to the selected segment (append when none).
  const [insertSide, setInsertSide] = useState<'before' | 'after'>('after');
  // Biome picker (design §20) — per-level environment re-skin. Open/close mirrors the
  // segment/object pickers; `biomeNewName` !== null shows the inline "name a new biome" input.
  const [biomePickerOpen, setBiomePickerOpen] = useState(false);
  const [biomeNewName, setBiomeNewName] = useState<string | null>(null);
  const [biomeBusy, setBiomeBusy] = useState(false);
  // First-run onboarding hint (dismiss persists) — decodes the level-editor jargon the eval flagged.
  const [helpDismissed, setHelpDismissed] = useState(() => {
    try {
      return localStorage.getItem('foundry:levelHelpDismissed') === '1';
    } catch {
      return false;
    }
  });
  const dismissHelp = (): void => {
    setHelpDismissed(true);
    try {
      localStorage.setItem('foundry:levelHelpDismissed', '1');
    } catch {
      /* private mode / no storage — hint just returns next mount */
    }
  };
  // Content items WITH their tags — lets a Content item DECLARE its role (a tag from the
  // ENTITY_ROLES vocab), so the picker shows it and placement defaults to the right role
  // instead of dumping everything as `prop`. Kind-agnostic: topdown entities ref Content too.
  const [paletteItems, setPaletteItems] = useState<PaletteItem[]>([]);
  useEffect(() => {
    let live = true;
    readContentPalette(client, gameId, project)
      .then((items) => {
        if (live) setPaletteItems(items);
      })
      .catch(() => {
        /* palette stays empty; picker falls back to bare names */
      });
    return () => {
      live = false;
    };
  }, [gameId, project]);
  const roleOf = useMemo(() => {
    const roles = new Set<string>(ENTITY_ROLES);
    const m = new Map<string, EntityRole>();
    for (const it of paletteItems) {
      const tag = it.tags.find((t) => roles.has(t.toLowerCase()));
      if (tag) m.set(it.name, tag.toLowerCase() as EntityRole);
    }
    return m;
  }, [paletteItems]);
  const hist = useRef<{ undo: string[]; redo: string[]; lastPush: number }>({
    undo: [],
    redo: [],
    lastPush: 0,
  });
  const [fixing, setFixing] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);
  const [reimagineOpen, setReimagineOpen] = useState(false);
  const [reimagineText, setReimagineText] = useState('');
  const [reimagining, setReimagining] = useState(false);
  const [judging, setJudging] = useState(false);
  const [judge, setJudge] = useState<import('@volley/crucible-ipc-types').DesignJudgeResult | null>(
    null,
  );
  const [judgeError, setJudgeError] = useState<string | null>(null);
  const doJudge = (): void => {
    setJudging(true);
    setJudgeError(null);
    judgeLevel(client, gameId, level.id)
      .then((r) => setJudge(r))
      .catch((e: unknown) => setJudgeError(e instanceof Error ? e.message : String(e)))
      .finally(() => setJudging(false));
  };
  const [revising, setRevising] = useState(false);
  const [revision, setRevision] = useState<
    import('@volley/crucible-ipc-types').DesignReviseResult | null
  >(null);
  const [reviseError, setReviseError] = useState<string | null>(null);
  const doRevise = (): void => {
    setRevising(true);
    setReviseError(null);
    setRevision(null);
    reviseLevel(client, gameId, level.id)
      .then((r) => setRevision(r))
      .catch((e: unknown) => setReviseError(e instanceof Error ? e.message : String(e)))
      .finally(() => setRevising(false));
  };

  // Re-seed local state when a different level is selected.
  useEffect(() => {
    setName(level.name);
    setTags(level.tags ?? []);
    setDataText(JSON.stringify(level.data, null, 2));
    setSel(null);
    setTdSel(null);
    hist.current = { undo: [], redo: [], lastPush: 0 };
  }, [level.id]);

  // Undoable edit: record the pre-change value (coalescing rapid bursts, e.g. a drag) then apply.
  const commitData = (next: string): void => {
    const h = hist.current;
    const now = Date.now();
    if (now - h.lastPush > 350) {
      h.undo.push(dataText);
      if (h.undo.length > 200) h.undo.shift();
      h.redo = [];
    }
    h.lastPush = now;
    setDataText(next);
  };
  /** Apply the proposed revision into the editor (autosaves via the data effect); PJ then
   *  reviews the previs + sends to build. Non-destructive until this click. */
  const applyRevision = (revised: unknown): void => {
    const data = (revised as { data?: unknown } | null)?.data;
    if (data === undefined) return;
    commitData(JSON.stringify(data, null, 2));
    setRevision(null);
    setJudge(null);
  };
  const undo = (): void => {
    const h = hist.current;
    const prev = h.undo.pop();
    if (prev === undefined) return;
    h.redo.push(dataText);
    h.lastPush = 0;
    setDataText(prev);
  };
  const redo = (): void => {
    const h = hist.current;
    const nxt = h.redo.pop();
    if (nxt === undefined) return;
    h.undo.push(dataText);
    h.lastPush = 0;
    setDataText(nxt);
  };
  const onKeyDown = (e: React.KeyboardEvent): void => {
    if ((e.metaKey || e.ctrlKey) && (e.key === 'z' || e.key === 'Z')) {
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
      return;
    }
    // Arrow-key nudge on the selected element (0.5m steps).
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const step = e.shiftKey ? 2 : 0.5;
    if (sel?.type === 'vertex' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      const lvl = result.parsed as SpatialLevel | undefined;
      const v = lvl?.vertices.find((x) => x.id === sel.id);
      if (v) {
        e.preventDefault();
        setVertexField(v.id, 'elevation', v.elevation + (e.key === 'ArrowUp' ? step : -step));
      }
    } else if (tdSel?.type === 'room' && e.key.startsWith('Arrow')) {
      const lvl = result.parsed as TopdownLevel | undefined;
      const r = lvl?.rooms.find((x) => x.id === tdSel.id);
      if (r) {
        e.preventDefault();
        if (e.key === 'ArrowUp') setRoomField(r.id, 'y', r.y - step);
        else if (e.key === 'ArrowDown') setRoomField(r.id, 'y', r.y + step);
        else if (e.key === 'ArrowLeft') setRoomField(r.id, 'x', r.x - step);
        else if (e.key === 'ArrowRight') setRoomField(r.id, 'x', r.x + step);
      }
    }
  };

  /** Kind is a per-level choice (design §17) — a level whose kind differs from the
   *  game's archetype validates/fixes against that kind's default 3Cs. */
  const kindMatchesFeel = isKindChoice(level.kind);
  const levelThreeCs = isKindChoice(level.kind) ? threeCsForKind(level.kind, threeCs) : threeCs;

  // Flat play position (pace ramp) so this level validates at the speed it runs at — must
  // match the nav's levelHealth, which uses the same progression index.
  const levelIndex = useMemo(() => {
    const entries = (progression as { entries?: { levelId: string }[] } | undefined)?.entries;
    if (!entries) return undefined;
    const i = entries.findIndex((e) => e.levelId === level.id);
    return i === -1 ? undefined : i;
  }, [progression, level.id]);

  const result = useMemo(
    () => validate(level.kind, dataText, levelThreeCs, palette, levelIndex, level.tags),
    [level.kind, dataText, levelThreeCs, palette, levelIndex, level.tags],
  );

  // Editor resilience: keep the last schema-valid parse so a mid-edit JSON typo dims the
  // viewport to its last-good state instead of blanking the whole editor (eval UX #6).
  const lastValidParsed = useRef<unknown>(undefined);
  useEffect(() => {
    if (result.parsed !== undefined) lastValidParsed.current = result.parsed;
  }, [result.parsed]);
  const previewParsed = result.parsed ?? lastValidParsed.current;
  const staleView = result.parseError !== null && previewParsed !== undefined;

  const canSave = result.parseError === null;
  const doSave = (): void => {
    if (!canSave) return;
    onChange({ ...level, name, tags, data: JSON.parse(dataText) });
  };

  // AUTOSAVE (eval 2026-07-10 UX #1): sections autosave, so levels must too — grid
  // paints/viewport drags previously vanished on navigation. Debounced, parse-gated
  // (a broken JSON edit is never persisted), and skipped when nothing differs from
  // the saved level so the wizard/save echo doesn't loop.
  const savedSnapshot = useRef<string>('');
  useEffect(() => {
    savedSnapshot.current = JSON.stringify({
      name: level.name,
      tags: level.tags ?? [],
      data: JSON.stringify(level.data),
    });
  }, [level]);
  useEffect(() => {
    if (!canSave) return;
    let parsedData: unknown;
    try {
      parsedData = JSON.parse(dataText);
    } catch {
      return;
    }
    const current = JSON.stringify({ name, tags, data: JSON.stringify(parsedData) });
    if (current === savedSnapshot.current) return;
    const timer = setTimeout(() => {
      savedSnapshot.current = current;
      onChange({ ...level, name, tags, data: parsedData });
    }, 800);
    return () => clearTimeout(timer);
    // Deps deliberately exclude level/onChange (identities churn per save); the
    // snapshot ref guards echo loops.
  }, [name, tags, dataText, canSave]);

  const errorCount = result.findings.filter((f) => f.severity === 'error').length;
  const doFix = (): void => {
    if (!result.parsed || !kindMatchesFeel) return;
    setFixing(true);
    setFixError(null);
    fixLevelWithAI(client, {
      gameId,
      threeCs: levelThreeCs,
      kind: isKindChoice(level.kind) ? level.kind : undefined,
      palette,
      data: result.parsed,
    })
      .then((res) => {
        commitData(JSON.stringify(res.data, null, 2));
        setFixError(
          res.findings.length > 0
            ? `Still ${res.findings.length} issue(s) after ${res.attempts} tries — tweak the rest by hand.`
            : null,
        );
      })
      .catch((e: unknown) => setFixError(e instanceof Error ? e.message : String(e)))
      .finally(() => setFixing(false));
  };

  const doReimagine = (): void => {
    if (!result.parsed || !kindMatchesFeel || !reimagineText.trim()) return;
    setReimagining(true);
    setFixError(null);
    reimagineLevelWithAI(client, {
      gameId,
      project,
      threeCs: levelThreeCs,
      kind: isKindChoice(level.kind) ? level.kind : undefined,
      data: result.parsed,
      instruction: reimagineText.trim(),
      levels: allLevels,
      progression,
      slot,
    })
      .then((res) => {
        commitData(JSON.stringify(res.data, null, 2));
        setReimagineOpen(false);
        setReimagineText('');
        setFixError(
          res.findings.length > 0
            ? `Reimagined, but ${res.findings.length} issue(s) remain after ${res.attempts} tries — see Validation.`
            : null,
        );
      })
      .catch((e: unknown) => setFixError(e instanceof Error ? e.message : String(e)))
      .finally(() => setReimagining(false));
  };

  // In-viewport gizmo: drag a vertex → new elevation, written straight into the JSON.
  const editVertex = (vertexId: string, elevation: number): void => {
    try {
      const d = JSON.parse(dataText) as { vertices?: { id: string; elevation: number }[] };
      const v = (d.vertices ?? []).find((x) => x.id === vertexId);
      if (v) {
        v.elevation = elevation;
        commitData(JSON.stringify(d, null, 2));
      }
    } catch {
      /* ignore while the JSON is mid-edit */
    }
  };

  // Gizmo X axis: dragging a vertex horizontally resizes the segment ENTERING it (its span),
  // i.e. moves the point along the track.
  const editVertexSpan = (vertexId: string, span: number): void => {
    try {
      const d = JSON.parse(dataText) as { segments?: { toId: string; span: number }[] };
      const s = (d.segments ?? []).find((x) => x.toId === vertexId);
      if (s) {
        s.span = span;
        commitData(JSON.stringify(d, null, 2));
      }
    } catch {
      /* ignore while the JSON is mid-edit */
    }
  };

  // Palette: insert a piece AFTER the selected segment (or append at the end), adding a
  // new vertex mid-chain, re-pointing the following segment, and renormalising t.
  const insertPiece = (
    makeSeg: (fromId: string, toId: string) => Record<string, unknown>,
    atIndex?: number,
    side: 'before' | 'after' = 'after',
  ): void => {
    try {
      const d = JSON.parse(dataText) as {
        vertices?: TrackVertex[];
        segments?: Record<string, unknown>[];
      };
      const verts = d.vertices ?? [];
      const segs = d.segments ?? [];
      const at = atIndex ?? (sel?.type === 'segment' ? sel.index : segs.length - 1);
      const out = spliceTrackSegment({ vertices: verts, segments: segs }, at, side, makeSeg);
      if (!out) return;
      commitData(JSON.stringify({ ...d, vertices: out.vertices, segments: out.segments }, null, 2));
      setSel({ type: 'segment', index: out.index }); // keep the new piece selected
    } catch {
      /* ignore while the JSON is mid-edit */
    }
  };
  /** Add a track segment relative to the selection: 'before'/'after' the selected
   *  segment, or appended at the end when nothing is selected. */
  const addSegmentSide = (kind: string, side: 'before' | 'after'): void =>
    insertPiece(
      (fromId, toId) => defaultSegment(kind, fromId, toId),
      undefined,
      sel?.type === 'segment' ? side : 'after',
    );
  const duplicateSegment = (index: number): void => {
    try {
      const d = JSON.parse(dataText) as { segments?: Record<string, unknown>[] };
      const src = (d.segments ?? [])[index];
      if (!src) return;
      const { fromId: _f, toId: _t, ...props } = src;
      insertPiece((fromId, toId) => ({ ...props, fromId, toId }), index);
    } catch {
      /* ignore */
    }
  };

  // Inspector: patch a field on the selected vertex / segment, written back into the JSON.
  const patchData = (mutate: (d: Record<string, unknown>) => void): void => {
    try {
      const d = JSON.parse(dataText) as Record<string, unknown>;
      mutate(d);
      commitData(JSON.stringify(d, null, 2));
    } catch {
      /* ignore while the JSON is mid-edit */
    }
  };
  const setVertexField = (id: string, key: string, value: number): void =>
    patchData((d) => {
      const v = ((d.vertices as Record<string, unknown>[] | undefined) ?? []).find(
        (x) => x.id === id,
      );
      if (v) v[key] = value;
    });
  const setSegmentField = (
    index: number,
    key: string,
    value: number | string | boolean | undefined,
  ): void =>
    patchData((d) => {
      const s = ((d.segments as Record<string, unknown>[] | undefined) ?? [])[index];
      if (!s) return;
      if (value === undefined) delete s[key];
      else s[key] = value;
    });
  const deleteSegment = (index: number): void =>
    patchData((d) => {
      const segs = (d.segments as unknown[] | undefined) ?? [];
      segs.splice(index, 1);
      d.segments = segs;
    });

  // ---- objects layer: entities (content placements) + terraces (overhead platforms) ----
  // Placing an object = picking a real Content item (never a generic placeholder), so the
  // ref always resolves. Role starts 'prop' and is refined in the inspector (design §17).
  const addObject = (ref: string, role: EntityRole = 'prop'): void => {
    try {
      const d = JSON.parse(dataText) as Record<string, unknown>;
      const list = (d.entities as Record<string, unknown>[] | undefined) ?? [];
      let n = list.length + 1;
      let id = `e${n}`;
      while (list.some((x) => x.id === id)) {
        n += 1;
        id = `e${n}`;
      }
      const ent: Record<string, unknown> = { id, ref, role, t: 0.5 };
      if (role === 'pickup') ent.elevation = 1.5; // a coin worth jumping for
      list.push(ent);
      d.entities = list;
      commitData(JSON.stringify(d, null, 2));
      setSel({ type: 'entity', id });
    } catch {
      /* ignore while the JSON is mid-edit */
    }
  };
  const addTerrace = (): void => {
    try {
      const d = JSON.parse(dataText) as Record<string, unknown>;
      const list = (d.terraces as Record<string, unknown>[] | undefined) ?? [];
      let n = list.length + 1;
      let id = `ter${n}`;
      while (list.some((x) => x.id === id)) {
        n += 1;
        id = `ter${n}`;
      }
      // seed at a comfortably-reachable height (below the jump apex) so it validates green.
      const apex =
        ('character' in levelThreeCs
          ? (levelThreeCs.character as { jumpHeight?: number }).jumpHeight
          : undefined) ?? 3;
      const height = Math.max(1, Math.round(apex * 0.6 * 10) / 10);
      list.push({ id, tStart: 0.4, tEnd: 0.6, height });
      d.terraces = list;
      commitData(JSON.stringify(d, null, 2));
      setSel({ type: 'terrace', id });
    } catch {
      /* ignore while the JSON is mid-edit */
    }
  };
  // ---- biome layer (design §20): a per-level environment re-skin. The level carries the
  // RESOLVED palette snapshot (data.biome = { name, ...colours }) so the built game is
  // self-contained; the skin's `biomes` registry is the reusable named-palette source. ----
  /** The biome name currently assigned to this level (undefined → Default / game palette). */
  const currentBiomeName = useMemo(() => {
    const b = (result.parsed as { biome?: { name?: string } } | undefined)?.biome;
    return typeof b?.name === 'string' ? b.name : undefined;
  }, [result.parsed]);
  const biomeNames = useMemo(
    () => Object.keys(previsSkin?.biomes ?? {}).sort((a, b) => a.localeCompare(b)),
    [previsSkin],
  );
  /** Default → drop data.biome so the level renders in the game's default palette. */
  const clearBiome = (): void =>
    patchData((d) => {
      delete d.biome;
    });
  /** Assign an existing registry biome — write the RESOLVED snapshot onto the level. */
  const assignBiome = (name: string): void => {
    const palette = previsSkin?.biomes?.[name];
    if (!palette) return;
    patchData((d) => {
      d.biome = { name, ...palette };
    });
    // TODO: apply-to-selected — offer to stamp this biome onto other selected levels here.
  };
  /** New biome: derive a palette from its name (+ Art Direction tone), add it to the skin's
   *  registry, and assign the resolved snapshot to THIS level. Scoped per-level by design. */
  const createBiome = async (rawName: string): Promise<void> => {
    const name = rawName.trim();
    if (!name || biomeBusy) return;
    setBiomeBusy(true);
    try {
      // Best-effort Art Direction prose for tone (helper tolerates undefined).
      let art: string | undefined;
      const artSection = project.sections.find((s) => s.id.toLowerCase().includes('art'));
      if (artSection)
        art = await readSection(client, gameId, artSection.filename).catch(() => undefined);
      const palette = await deriveBiomePalette(client, gameId, name, art);
      // (a) add to the skin registry (reusable across levels) and lift to the owner.
      if (previsSkin && onPrevisSkinChange) {
        const nextSkin: PrevisSkin = {
          ...previsSkin,
          biomes: { ...previsSkin.biomes, [name]: palette },
        };
        await writePrevisSkin(client, gameId, nextSkin).catch(() => undefined);
        onPrevisSkinChange(nextSkin);
      }
      // (b) assign the resolved snapshot to this level.
      patchData((d) => {
        d.biome = { name, ...palette };
      });
    } finally {
      setBiomeBusy(false);
      setBiomeNewName(null);
      setBiomePickerOpen(false);
    }
  };

  const moveEntity = (id: string, t: number, elevation: number): void =>
    patchData((d) => {
      const e = ((d.entities as Record<string, unknown>[] | undefined) ?? []).find(
        (x) => x.id === id,
      );
      if (e) {
        e.t = t;
        e.elevation = elevation;
      }
    });
  const setEntityField = (id: string, key: string, value: number | string | undefined): void =>
    patchData((d) => {
      const e = ((d.entities as Record<string, unknown>[] | undefined) ?? []).find(
        (x) => x.id === id,
      );
      if (!e) return;
      if (value === undefined || value === '') delete e[key];
      else e[key] = value;
    });
  const deleteEntity = (id: string): void =>
    patchData((d) => {
      d.entities = ((d.entities as { id: string }[] | undefined) ?? []).filter((x) => x.id !== id);
    });
  const moveTerrace = (id: string, tStart: number, tEnd: number, height: number): void =>
    patchData((d) => {
      const t = ((d.terraces as Record<string, unknown>[] | undefined) ?? []).find(
        (x) => x.id === id,
      );
      if (t) {
        t.tStart = tStart;
        t.tEnd = tEnd;
        t.height = height;
      }
    });
  const setTerraceField = (id: string, key: string, value: number): void =>
    patchData((d) => {
      const t = ((d.terraces as Record<string, unknown>[] | undefined) ?? []).find(
        (x) => x.id === id,
      );
      if (t) t[key] = value;
    });
  const deleteTerrace = (id: string): void =>
    patchData((d) => {
      d.terraces = ((d.terraces as { id: string }[] | undefined) ?? []).filter((x) => x.id !== id);
    });

  // ---- topdown-2d helpers (floor plans) ----
  type RoomRec = Record<string, unknown> & { id?: string; x?: number; y?: number; w?: number };
  const setRoomField = (id: string, key: string, value: number | string): void =>
    patchData((d) => {
      const r = ((d.rooms as RoomRec[] | undefined) ?? []).find((x) => x.id === id);
      if (r) r[key] = value;
    });
  const moveRoom = (id: string, x: number, y: number): void =>
    patchData((d) => {
      const r = ((d.rooms as RoomRec[] | undefined) ?? []).find((x) => x.id === id);
      if (r) {
        r.x = x;
        r.y = y;
      }
    });
  const setCorridorField = (index: number, key: string, value: number | string | undefined): void =>
    patchData((d) => {
      const c = ((d.corridors as Record<string, unknown>[] | undefined) ?? [])[index];
      if (!c) return;
      if (value === undefined || value === '') delete c[key];
      else c[key] = value;
    });
  const deleteCorridor = (index: number): void =>
    patchData((d) => {
      const cs = (d.corridors as unknown[] | undefined) ?? [];
      cs.splice(index, 1);
      d.corridors = cs;
    });
  const addRoom = (): void =>
    patchData((d) => {
      const rooms = (d.rooms as RoomRec[] | undefined) ?? [];
      const last = rooms[rooms.length - 1];
      const id = `room${rooms.length + 1}`;
      rooms.push({
        id,
        x: ((last?.x as number) ?? 0) + 12,
        y: (last?.y as number) ?? 0,
        w: 10,
        h: 8,
        kind: 'normal',
      });
      d.rooms = rooms;
      if (last?.id) {
        d.corridors = [
          ...((d.corridors as unknown[] | undefined) ?? []),
          { fromId: last.id, toId: id, width: 2 },
        ];
      }
    });
  const addKeyToRoom = (roomId: string): void =>
    patchData((d) => {
      const keys = (d.keys as Record<string, unknown>[] | undefined) ?? [];
      keys.push({ id: `key${keys.length + 1}`, roomId });
      d.keys = keys;
    });
  /** Rename a room id safely — updates corridors + keys that reference it. */
  const renameRoom = (oldId: string, newId: string): void => {
    const clean = newId.trim();
    if (!clean || clean === oldId) return;
    patchData((d) => {
      const rooms = (d.rooms as RoomRec[] | undefined) ?? [];
      if (rooms.some((r) => r.id === clean)) return; // id taken — no-op
      const r = rooms.find((x) => x.id === oldId);
      if (!r) return;
      r.id = clean;
      for (const c of (d.corridors as Record<string, unknown>[] | undefined) ?? []) {
        if (c.fromId === oldId) c.fromId = clean;
        if (c.toId === oldId) c.toId = clean;
      }
      for (const k of (d.keys as Record<string, unknown>[] | undefined) ?? []) {
        if (k.roomId === oldId) k.roomId = clean;
      }
    });
    setTdSel({ type: 'room', id: clean });
  };
  const duplicateRoom = (roomId: string): void =>
    patchData((d) => {
      const rooms = (d.rooms as RoomRec[] | undefined) ?? [];
      const src = rooms.find((r) => r.id === roomId);
      if (!src) return;
      let n = 2;
      let id = `${roomId}-${n}`;
      while (rooms.some((r) => r.id === id)) {
        n += 1;
        id = `${roomId}-${n}`;
      }
      rooms.push({
        ...src,
        id,
        x: ((src.x as number) ?? 0) + 2,
        y: ((src.y as number) ?? 0) + 2,
        kind: 'normal',
      });
    });

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4"
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <div className="flex items-center gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="flex-1 rounded border border-crucible-border bg-transparent px-2 py-1 text-sm text-crucible-fg"
          aria-label="Level name"
        />
        <span className="rounded-full border border-crucible-accent/40 bg-crucible-accent/15 px-2 py-0.5 text-[11px] text-crucible-accent-hover">
          {kindShortLabel(level.kind)}
        </span>
        <button
          type="button"
          onClick={doSave}
          disabled={!canSave}
          className="rounded border border-crucible-border px-2 py-1 text-sm text-crucible-fg hover:bg-crucible-bg-elevated disabled:opacity-50"
        >
          Save
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="rounded border border-crucible-border px-2 py-1 text-sm text-red-400 hover:bg-crucible-bg-elevated"
        >
          Delete
        </button>
      </div>

      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <div className="min-w-0 flex-1">
            <TagsField
              tags={tags}
              onCommit={setTags}
              placeholder="level-1, tutorial, fire (comma-separated — for cross-refs + the build AI)"
              suggestions={tagSuggestions}
            />
          </div>
          {onTagClick &&
            tags.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => onTagClick(t)}
                title={`Find everything tagged ${t}`}
                className="shrink-0 rounded-full border border-crucible-border px-1.5 py-0.5 text-[10px] text-crucible-accent-hover hover:bg-crucible-bg-elevated"
              >
                #{t}
              </button>
            ))}
        </div>
        {kindMatchesFeel && (
          <button
            type="button"
            onClick={() => setReimagineOpen((o) => !o)}
            className="shrink-0 rounded border border-crucible-accent/50 bg-crucible-accent/10 px-2 py-1 text-xs text-crucible-accent-hover hover:bg-crucible-accent/20"
          >
            ✨ Reimagine
          </button>
        )}
        {kindMatchesFeel && judgeEnabled && (
          <button
            type="button"
            onClick={doJudge}
            disabled={judging}
            title="Ask an ensemble of AI design judges to score this level for FUN & fit (cadence, risk/reward, encounters) and suggest concrete placement fixes"
            className="shrink-0 rounded border border-crucible-accent/50 bg-crucible-accent/10 px-2 py-1 text-xs text-crucible-accent-hover hover:bg-crucible-accent/20 disabled:opacity-50"
          >
            {judging ? 'Reviewing…' : '⚖ Review design'}
          </button>
        )}
        {kindMatchesFeel && judgeEnabled && level.kind === 'spatial-2d' && (
          <button
            type="button"
            onClick={doRevise}
            disabled={revising}
            title="Run the generate→sim→critique→revise loop: judge this level, have the AI rewrite it to fix the top issues, re-validate solvability and re-judge, then PROPOSE the improved level for you to apply and send to build"
            className="shrink-0 rounded border border-crucible-accent/50 bg-crucible-accent/10 px-2 py-1 text-xs text-crucible-accent-hover hover:bg-crucible-accent/20 disabled:opacity-50"
          >
            {revising ? 'Revising…' : '✦ Auto-revise'}
          </button>
        )}
      </div>

      {judgeError && (
        <p className="rounded border border-error/25 bg-error/12 p-2 text-xs text-error">
          Design review failed: {judgeError}
        </p>
      )}
      {judge && (
        <div
          className="space-y-2 rounded-lg border border-crucible-accent/40 bg-crucible-accent/5 p-3"
          data-testid="level-design-judge"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-crucible-fg">Design review</span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                judge.score >= 75
                  ? 'bg-emerald-300/15 text-emerald-300'
                  : judge.score >= 50
                    ? 'bg-amber-300/15 text-amber-200'
                    : 'bg-error/15 text-error'
              }`}
            >
              {judge.score}/100
            </span>
            <span className="text-[11px] text-crucible-muted">
              {judge.votes} judge{judge.votes === 1 ? '' : 's'}
              {judge.disagreement ? ' · split verdict — low confidence' : ''}
            </span>
            <button
              type="button"
              onClick={() => setJudge(null)}
              className="ml-auto px-1 text-xs text-crucible-muted hover:text-crucible-fg"
            >
              ✕
            </button>
          </div>
          {judge.summary && <p className="text-xs text-crucible-muted">{judge.summary}</p>}
          {judge.metrics && (
            <p className="text-[11px] text-crucible-muted" data-testid="judge-sim-metrics">
              <span className="uppercase tracking-wide opacity-70">sim</span> · cadence ~
              {judge.metrics.cadenceMeanSec}s (dead {judge.metrics.longestDeadStretchSec}s) · margin{' '}
              {Math.round(judge.metrics.tightestMarginFrac * 100)}%
              {judge.metrics.precisionTuned ? ' ⚠ precision-tuned' : ''} ·{' '}
              {judge.metrics.routeCount} route{judge.metrics.routeCount === 1 ? '' : 's'}
              {judge.metrics.unreachableCoins
                ? ` · ${judge.metrics.unreachableCoins} dead coins`
                : ''}
            </p>
          )}
          {judge.perAxis.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {judge.perAxis.map((a) => (
                <span
                  key={a.axis}
                  title={a.note}
                  className="rounded-full border border-crucible-border px-1.5 py-0.5 text-[10px] text-crucible-muted"
                >
                  {a.axis} {a.score}
                </span>
              ))}
            </div>
          )}
          {judge.issues.length > 0 ? (
            <ul className="space-y-1.5">
              {judge.issues.map((it, i) => (
                <li key={i} className="text-xs">
                  <span
                    className={
                      it.severity === 'high'
                        ? 'text-error'
                        : it.severity === 'medium'
                          ? 'text-amber-200'
                          : 'text-crucible-muted'
                    }
                  >
                    [{it.axis}] {it.where}
                  </span>{' '}
                  <span className="text-crucible-fg">{it.problem}</span>{' '}
                  <span className="text-crucible-muted">→ {it.fix}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-emerald-300">No placement issues flagged — plays well.</p>
          )}
        </div>
      )}

      {reviseError && (
        <p className="rounded border border-error/25 bg-error/12 p-2 text-xs text-error">
          Auto-revise failed: {reviseError}
        </p>
      )}
      {revision && (
        <div
          className="space-y-2 rounded-lg border border-crucible-accent/40 bg-crucible-accent/5 p-3"
          data-testid="level-auto-revise"
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-crucible-fg">Auto-revise</span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                revision.improved
                  ? 'bg-emerald-300/15 text-emerald-300'
                  : 'bg-crucible-border/40 text-crucible-muted'
              }`}
            >
              {revision.originalScore} → {revision.finalScore}
              {revision.improved ? ` (+${revision.finalScore - revision.originalScore})` : ''}
            </span>
            <button
              type="button"
              onClick={() => setRevision(null)}
              className="ml-auto px-1 text-xs text-crucible-muted hover:text-crucible-fg"
            >
              ✕
            </button>
          </div>
          {revision.rounds.length > 0 && (
            <ol className="space-y-1">
              {revision.rounds.map((r) => (
                <li key={r.round} className="text-[11px] text-crucible-muted">
                  <span className={r.accepted ? 'text-emerald-300' : 'text-crucible-muted'}>
                    {r.accepted ? '✓' : '—'} round {r.round}: {r.score}/100
                  </span>
                  {r.changeNote ? (
                    <span className="text-crucible-fg"> · {r.changeNote}</span>
                  ) : null}
                  {r.rejectedReason ? (
                    <span className="opacity-70"> ({r.rejectedReason})</span>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
          {revision.improved && revision.revised ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => applyRevision(revision.revised)}
                className="rounded border border-emerald-300/50 bg-emerald-300/10 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-300/20"
              >
                Apply revision
              </button>
              <span className="text-[11px] text-crucible-muted">
                Loads into the editor + previs (autosaves). Review, then Send to build.
              </span>
            </div>
          ) : (
            <p className="text-xs text-crucible-muted">
              No improvement found this pass — the level already holds up, or the rewrites
              didn&apos;t beat it. The original is unchanged.
            </p>
          )}
        </div>
      )}

      {reimagineOpen && (
        <div className="flex items-start gap-2 rounded-lg border border-crucible-accent/40 bg-crucible-accent/5 p-2">
          <textarea
            value={reimagineText}
            onChange={(e) => setReimagineText(e.target.value)}
            placeholder={
              slot
                ? `Reimagine level ${slot.label}… e.g. "turn the middle into a lava cave", "add more verticality"`
                : 'e.g. "turn the middle into a lava cave", "add more verticality"'
            }
            rows={2}
            autoFocus
            className="min-w-0 flex-1 rounded border border-crucible-border bg-crucible-bg p-2 text-xs text-crucible-fg outline-none focus:border-crucible-accent"
          />
          <button
            type="button"
            onClick={doReimagine}
            disabled={reimagining || !reimagineText.trim()}
            className="shrink-0 rounded border border-crucible-border px-2 py-1 text-xs text-crucible-fg hover:bg-crucible-bg-elevated disabled:opacity-50"
          >
            {reimagining ? 'Reimagining…' : 'Go'}
          </button>
          <button
            type="button"
            onClick={() => setReimagineOpen(false)}
            disabled={reimagining}
            className="shrink-0 px-1 py-1 text-xs text-crucible-muted hover:text-crucible-fg"
          >
            ✕
          </button>
        </div>
      )}

      {/* Validation panel — live crucible-levels validator */}
      <div className="rounded-lg border border-crucible-border p-2">
        <div className="mb-1 flex items-center gap-2 text-[11px] uppercase tracking-wide text-crucible-muted">
          Validation
          {result.parseError === null && result.ok && (
            <span
              className="text-emerald-300"
              title="The geometry is reachable under the 3Cs. This is not a playtest — the level isn't rendered in the running game yet."
            >
              ✓ solvable · geometry only
            </span>
          )}
          {errorCount > 0 && kindMatchesFeel && result.parsed !== undefined && (
            <button
              type="button"
              onClick={doFix}
              disabled={fixing}
              className="ml-auto inline-flex items-center gap-1 rounded border border-crucible-accent/50 bg-crucible-accent/10 px-2 py-0.5 text-[11px] normal-case text-crucible-accent-hover hover:bg-crucible-accent/20 disabled:opacity-60"
            >
              {fixing && <span className="size-1.5 animate-pulse rounded-full bg-amber-300" />}
              {fixing ? 'Fixing…' : '✨ Fix with AI'}
            </button>
          )}
        </div>
        {fixError && <p className="mb-1 text-[11px] text-amber-300">{fixError}</p>}
        {result.parseError ? (
          <p className="text-xs text-red-400">
            JSON error: {result.parseError}
            {staleView && (
              <span className="text-crucible-muted"> — showing your last valid version below.</span>
            )}
          </p>
        ) : result.findings.length === 0 ? (
          <p className="text-xs text-emerald-300">No issues found.</p>
        ) : (
          <ul className="space-y-1.5">
            {result.findings.map((f, i) => {
              const h = humanizeFinding(f);
              return (
                <li key={i} className="text-xs">
                  <div className={`font-medium ${SEVERITY_CLASS[h.severity] ?? ''}`}>{h.title}</div>
                  {h.fix && <div className="text-crucible-muted">→ {h.fix}</div>}
                  <details className="text-[10px] text-crucible-muted/70">
                    <summary className="cursor-pointer">details</summary>
                    <span className="font-mono">{f.code}</span>
                    {f.at?.vertexId ? ` @${f.at.vertexId}` : ''} — {h.detail}
                  </details>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* First-run onboarding — decode the jargon + show the intended flow (dismissible). */}
      {level.kind === 'spatial-2d' && !helpDismissed && (
        <div className="rounded-lg border border-crucible-accent/40 bg-crucible-accent/5 p-2.5 text-[11px] leading-relaxed text-crucible-muted">
          <div className="mb-1 flex items-center justify-between">
            <span className="font-medium text-crucible-fg">New to the level editor?</span>
            <button
              type="button"
              onClick={dismissHelp}
              className="text-crucible-muted hover:text-crucible-fg"
            >
              dismiss
            </button>
          </div>
          <p className="m-0">
            A level places <b>objects</b> (Content items — coins, enemies) on a <b>track</b> (the
            spine), validated against your <b>3Cs</b> feel. Build order: <b>Content</b> (what
            exists) → <b>3Cs</b> (feel) → <b>Levels</b> → <b>Progression</b> (order) →{' '}
            <b>Screens</b> (menus). Terms: <b>fence</b> = jump-over obstacle · <b>terrace</b> =
            overhead alternate route · <b>Previs</b> = game-look preview. Hover any palette button
            for what it does.
          </p>
        </div>
      )}

      {/* Palette — append track pieces (Content⇄Levels: hazards become segments) */}
      {level.kind === 'spatial-2d' && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wide text-crucible-muted">Track</span>
          <div className="relative">
            <button
              type="button"
              onClick={() => setSegPickerOpen((o) => !o)}
              title="Add a track segment (platform, gap, fence, reversal, collapse)"
              className="rounded-full border border-crucible-border px-2 py-0.5 text-[11px] text-crucible-fg hover:bg-crucible-bg-elevated"
            >
              + segment ▾
            </button>
            {segPickerOpen && (
              <div className="absolute left-0 top-7 z-20 w-72 rounded-lg border border-crucible-border bg-crucible-bg-elevated p-2 shadow-xl">
                {sel?.type === 'segment' ? (
                  <div className="mb-1.5 flex items-center gap-1.5 text-[11px] text-crucible-muted">
                    <span>Insert</span>
                    <div className="flex overflow-hidden rounded-full border border-crucible-border">
                      {(['before', 'after'] as const).map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setInsertSide(s)}
                          className={`px-2 py-0.5 text-[10px] ${
                            insertSide === s
                              ? 'bg-crucible-accent/20 text-crucible-accent-hover'
                              : 'text-crucible-fg hover:bg-crucible-bg'
                          }`}
                        >
                          {s === 'before' ? '◀ left' : 'right ▶'}
                        </button>
                      ))}
                    </div>
                    <span className="text-crucible-muted/70">of the selected piece</span>
                  </div>
                ) : (
                  <p className="mb-1.5 px-1 text-[10px] text-crucible-muted">
                    Adds to the end — select a segment first to insert left/right of it.
                  </p>
                )}
                <div className="max-h-64 overflow-y-auto">
                  {SEGMENT_PALETTE.map((k) => {
                    const desc = SEGMENT_HELP[k].split('— ')[1] ?? SEGMENT_HELP[k];
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => {
                          addSegmentSide(k, insertSide);
                          setSegPickerOpen(false);
                        }}
                        className="block w-full rounded px-2 py-1 text-left hover:bg-crucible-accent/15"
                      >
                        <span className="text-xs capitalize text-crucible-fg">{k}</span>
                        <span className="mt-0.5 block text-[10px] leading-snug text-crucible-muted">
                          {desc}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <span className="mx-1 h-4 w-px bg-crucible-border" aria-hidden />
          <span className="text-[11px] uppercase tracking-wide text-crucible-muted">Objects</span>
          <button
            type="button"
            onClick={addTerrace}
            title="Overhead one-way platform — an alternate high route (Mario-style)"
            className="rounded-full border border-[#c792ea]/50 bg-[#c792ea]/10 px-2 py-0.5 text-[11px] text-[#c792ea] hover:bg-[#c792ea]/20"
          >
            + terrace
          </button>
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setObjFilter('');
                setObjPickerOpen((o) => !o);
              }}
              title="Place a Content item (enemy, pickup, checkpoint…) on the track"
              className="rounded-full border border-crucible-border px-2 py-0.5 text-[11px] text-crucible-fg hover:bg-crucible-bg-elevated"
            >
              + object ▾
            </button>
            {objPickerOpen && (
              <div className="absolute left-0 top-7 z-20 w-56 rounded-lg border border-crucible-border bg-crucible-bg-elevated p-2 shadow-xl">
                {(() => {
                  const names =
                    paletteItems.length > 0
                      ? paletteItems.map((i) => i.name)
                      : [...(palette ?? [])];
                  const items = [...new Set(names)].sort((a, b) => a.localeCompare(b));
                  const q = objFilter.trim().toLowerCase();
                  const shown = q ? items.filter((n) => n.toLowerCase().includes(q)) : items;
                  return (
                    <>
                      <input
                        autoFocus
                        value={objFilter}
                        onChange={(e) => setObjFilter(e.target.value)}
                        placeholder="Search Content items…"
                        className="mb-1.5 w-full rounded border border-crucible-border bg-crucible-bg px-2 py-1 text-xs text-crucible-fg outline-none focus:border-crucible-accent"
                      />
                      <div className="max-h-56 overflow-y-auto">
                        {items.length === 0 ? (
                          <p className="px-1 py-2 text-[11px] text-crucible-muted">
                            No Content items yet — add enemies/pickups in the Content section first.
                          </p>
                        ) : shown.length === 0 ? (
                          <p className="px-1 py-2 text-[11px] text-crucible-muted">No match.</p>
                        ) : (
                          shown.map((n) => {
                            const role = roleOf.get(n);
                            return (
                              <button
                                key={n}
                                type="button"
                                onClick={() => {
                                  addObject(n, role ?? 'prop');
                                  setObjPickerOpen(false);
                                }}
                                className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs text-crucible-fg hover:bg-crucible-accent/15"
                              >
                                <span className="truncate">{n}</span>
                                <span
                                  className={`shrink-0 font-mono text-[10px] ${role ? 'text-crucible-accent-hover' : 'text-crucible-muted/60'}`}
                                >
                                  {role ?? 'prop?'}
                                </span>
                              </button>
                            );
                          })
                        )}
                      </div>
                      <p className="mt-1.5 border-t border-crucible-border pt-1.5 text-[10px] text-crucible-muted">
                        Role comes from the Content item&apos;s tag
                        (enemy/pickup/hazard/checkpoint). Untagged shows “prop?” — tag it in Content
                        to fix.
                      </p>
                    </>
                  );
                })()}
              </div>
            )}
          </div>
          <span className="mx-1 h-4 w-px bg-crucible-border" aria-hidden />
          <span className="text-[11px] uppercase tracking-wide text-crucible-muted">Biome</span>
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                setBiomeNewName(null);
                setBiomePickerOpen((o) => !o);
              }}
              title="Re-skin this level's environment (snowy, cave, lava…) — per level"
              className="rounded-full border border-crucible-border px-2 py-0.5 text-[11px] text-crucible-fg hover:bg-crucible-bg-elevated"
            >
              {currentBiomeName ?? 'Default'} ▾
            </button>
            {biomePickerOpen && (
              <div className="absolute left-0 top-7 z-20 w-56 rounded-lg border border-crucible-border bg-crucible-bg-elevated p-2 shadow-xl">
                {biomeNewName === null ? (
                  <>
                    <div className="max-h-56 overflow-y-auto">
                      <button
                        type="button"
                        onClick={() => {
                          clearBiome();
                          setBiomePickerOpen(false);
                        }}
                        className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs hover:bg-crucible-accent/15 ${
                          !currentBiomeName ? 'text-crucible-accent-hover' : 'text-crucible-fg'
                        }`}
                      >
                        <span>Default</span>
                        <span className="shrink-0 text-[10px] text-crucible-muted/60">
                          game palette
                        </span>
                      </button>
                      {biomeNames.map((n) => {
                        const nUsed = usageIndex ? usageCount(biomeSites(usageIndex, n)) : 0;
                        return (
                          <button
                            key={n}
                            type="button"
                            onClick={() => {
                              assignBiome(n);
                              setBiomePickerOpen(false);
                            }}
                            title={
                              nUsed > 0
                                ? `Used in ${nUsed} level${nUsed === 1 ? '' : 's'}`
                                : undefined
                            }
                            className={`flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs capitalize hover:bg-crucible-accent/15 ${
                              currentBiomeName === n
                                ? 'text-crucible-accent-hover'
                                : 'text-crucible-fg'
                            }`}
                          >
                            <span className="truncate">{n}</span>
                            {nUsed > 0 && (
                              <span className="shrink-0 text-[10px] normal-case text-crucible-muted/60">
                                used in {nUsed}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      onClick={() => setBiomeNewName('')}
                      className="mt-1.5 block w-full rounded border-t border-crucible-border px-2 pt-2 text-left text-[11px] text-crucible-accent-hover hover:bg-crucible-accent/15"
                    >
                      ＋ New biome…
                    </button>
                  </>
                ) : (
                  <div>
                    <p className="mb-1.5 px-1 text-[10px] text-crucible-muted">
                      Name the biome — a palette is derived from the name + Art Direction.
                    </p>
                    <input
                      autoFocus
                      value={biomeNewName}
                      disabled={biomeBusy}
                      onChange={(e) => setBiomeNewName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') void createBiome(biomeNewName);
                        else if (e.key === 'Escape') setBiomeNewName(null);
                      }}
                      placeholder="e.g. snowy, cave, lava…"
                      className="mb-1.5 w-full rounded border border-crucible-border bg-crucible-bg px-2 py-1 text-xs text-crucible-fg outline-none focus:border-crucible-accent disabled:opacity-50"
                    />
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => setBiomeNewName(null)}
                        disabled={biomeBusy}
                        className="rounded px-2 py-0.5 text-[11px] text-crucible-muted hover:bg-crucible-bg disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => void createBiome(biomeNewName)}
                        disabled={biomeBusy || !biomeNewName.trim()}
                        className="rounded-full border border-crucible-accent/50 bg-crucible-accent/10 px-2 py-0.5 text-[11px] text-crucible-accent-hover hover:bg-crucible-accent/20 disabled:opacity-50"
                      >
                        {biomeBusy ? 'Deriving…' : 'Create'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Viewport — spatial-2d track view (zoom/pan, drag-to-edit, click-to-select).
          On a JSON typo it dims to the last-valid parse (non-interactive) instead of vanishing. */}
      {level.kind === 'spatial-2d' && previewParsed ? (
        <div className={staleView ? 'pointer-events-none opacity-50' : undefined}>
          <LevelViewport
            level={previewParsed as SpatialLevel}
            findings={staleView ? [] : result.findings}
            selected={sel}
            threeCs={levelThreeCs}
            onEditVertex={editVertex}
            onEditVertexSpan={editVertexSpan}
            onMoveEntity={moveEntity}
            onMoveTerrace={moveTerrace}
            onSelect={setSel}
            skin={previsSkin}
            images={previsImages}
          />
        </div>
      ) : null}

      {/* topdown-2d — floor-plan add bar + plan-view viewport + inspector */}
      {level.kind === 'topdown-2d' && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] uppercase tracking-wide text-crucible-muted">Add</span>
          <button
            type="button"
            onClick={addRoom}
            className="rounded-full border border-crucible-border px-2 py-0.5 text-[11px] text-crucible-fg hover:bg-crucible-bg-elevated"
          >
            + room
          </button>
          {tdSel?.type === 'room' && (
            <button
              type="button"
              onClick={() => addKeyToRoom(tdSel.id)}
              className="rounded-full border border-crucible-border px-2 py-0.5 text-[11px] text-crucible-fg hover:bg-crucible-bg-elevated"
            >
              + key in {tdSel.id}
            </button>
          )}
        </div>
      )}
      {level.kind === 'topdown-2d' && previewParsed ? (
        <div className={staleView ? 'pointer-events-none opacity-50' : undefined}>
          <TopdownViewport
            level={previewParsed as TopdownLevel}
            findings={staleView ? [] : result.findings}
            selected={tdSel}
            onMoveRoom={moveRoom}
            onSelect={setTdSel}
            skin={previsSkin}
          />
        </div>
      ) : null}
      {/* Data levels get the spreadsheet-style grid; JSON below stays the escape hatch. */}
      {level.kind === 'data-table' && result.parsed ? (
        <DataTableEditor
          data={result.parsed as DataTableLevel}
          onChange={(d) => commitData(JSON.stringify(d, null, 2))}
        />
      ) : null}
      {/* Board levels get the tile painter. */}
      {level.kind === 'board-grid' && result.parsed ? (
        <BoardGridEditor
          data={result.parsed as BoardGridLevel}
          onChange={(d) => commitData(JSON.stringify(d, null, 2))}
        />
      ) : null}
      {level.kind === 'topdown-2d' && result.parsed && tdSel
        ? (() => {
            const lvl = result.parsed as TopdownLevel;
            const numField = (
              label: string,
              value: number,
              onChange: (v: number) => void,
              step = 0.5,
            ) => (
              <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                <span className="text-crucible-muted">{label}</span>
                <input
                  type="number"
                  step={step}
                  value={value}
                  onChange={(e) => onChange(Number(e.target.value))}
                  className="w-24 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-right text-sm text-crucible-fg"
                />
              </label>
            );
            if (tdSel.type === 'room') {
              const r = lvl.rooms.find((x) => x.id === tdSel.id);
              if (!r) return null;
              return (
                <div className="rounded-lg border border-crucible-accent/40 bg-crucible-accent/5 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-crucible-fg">
                      Room <code className="text-crucible-accent-hover">{r.id}</code>
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => duplicateRoom(r.id)}
                        className="text-[11px] text-crucible-muted hover:text-crucible-fg"
                      >
                        duplicate
                      </button>
                      <button
                        type="button"
                        onClick={() => setTdSel(null)}
                        className="text-[11px] text-crucible-muted hover:text-crucible-fg"
                      >
                        close
                      </button>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                      <span className="text-crucible-muted">Id</span>
                      <input
                        defaultValue={r.id}
                        key={r.id}
                        onBlur={(e) => renameRoom(r.id, e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter')
                            renameRoom(r.id, (e.target as HTMLInputElement).value);
                          e.stopPropagation();
                        }}
                        className="w-32 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-sm text-crucible-fg"
                      />
                    </label>
                    <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                      <span className="text-crucible-muted">Kind</span>
                      <select
                        value={r.kind}
                        onChange={(e) => setRoomField(r.id, 'kind', e.target.value)}
                        className="w-32 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-sm text-crucible-fg"
                      >
                        {['start', 'goal', 'combat', 'puzzle', 'boss', 'normal'].map((k) => (
                          <option key={k} value={k}>
                            {k}
                          </option>
                        ))}
                      </select>
                    </label>
                    {numField('X (m)', r.x, (v) => setRoomField(r.id, 'x', v))}
                    {numField('Y (m)', r.y, (v) => setRoomField(r.id, 'y', v))}
                    {numField('Width (m)', r.w, (v) => setRoomField(r.id, 'w', v))}
                    {numField('Height (m)', r.h, (v) => setRoomField(r.id, 'h', v))}
                  </div>
                  <p className="mt-1 text-[11px] text-crucible-muted">
                    Drag the room in the plan to move it. Keys here:{' '}
                    {lvl.keys
                      .filter((k) => k.roomId === r.id)
                      .map((k) => k.id)
                      .join(', ') || 'none'}
                  </p>
                </div>
              );
            }
            const c = lvl.corridors[tdSel.index];
            if (!c) return null;
            return (
              <div className="rounded-lg border border-crucible-accent/40 bg-crucible-accent/5 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-crucible-fg">
                    Corridor{' '}
                    <code className="text-crucible-muted">
                      {c.fromId} → {c.toId}
                    </code>
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        deleteCorridor(tdSel.index);
                        setTdSel(null);
                      }}
                      className="text-[11px] text-red-400 hover:text-red-300"
                    >
                      delete
                    </button>
                    <button
                      type="button"
                      onClick={() => setTdSel(null)}
                      className="text-[11px] text-crucible-muted hover:text-crucible-fg"
                    >
                      close
                    </button>
                  </div>
                </div>
                <div className="grid gap-2">
                  {numField('Width (m)', c.width, (v) => setCorridorField(tdSel.index, 'width', v))}
                  <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                    <span className="text-crucible-muted">Locked by key</span>
                    <select
                      value={c.lockedBy ?? ''}
                      onChange={(e) => setCorridorField(tdSel.index, 'lockedBy', e.target.value)}
                      className="w-32 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-sm text-crucible-fg"
                    >
                      <option value="">(open)</option>
                      {lvl.keys.map((k) => (
                        <option key={k.id} value={k.id}>
                          {k.id}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>
            );
          })()
        : null}

      {/* Inspector — click a point/segment in the viewport to edit its variables */}
      {level.kind === 'spatial-2d' && result.parsed && sel
        ? (() => {
            const lvl = result.parsed as SpatialLevel;
            if (sel.type === 'vertex') {
              const v = lvl.vertices.find((x) => x.id === sel.id);
              if (!v) return null;
              return (
                <div className="rounded-lg border border-crucible-accent/40 bg-crucible-accent/5 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-crucible-fg">
                      Vertex <code className="text-crucible-accent-hover">{v.id}</code>
                    </span>
                    <button
                      type="button"
                      onClick={() => setSel(null)}
                      className="text-[11px] text-crucible-muted hover:text-crucible-fg"
                    >
                      close
                    </button>
                  </div>
                  <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                    <span className="text-crucible-muted">Elevation (m)</span>
                    <input
                      type="number"
                      step={0.5}
                      value={v.elevation}
                      onChange={(e) => setVertexField(v.id, 'elevation', Number(e.target.value))}
                      className="w-24 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-right text-sm text-crucible-fg"
                    />
                  </label>
                  <p className="mt-1 text-[11px] text-crucible-muted">
                    t = {v.t.toFixed(3)} (position along the track). Drag the point in the view to
                    change elevation too.
                  </p>
                </div>
              );
            }
            if (sel.type === 'entity') {
              const en = (lvl.entities ?? []).find((x) => x.id === sel.id);
              if (!en) return null;
              return (
                <div className="rounded-lg border border-crucible-accent/40 bg-crucible-accent/5 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-crucible-fg">
                      Object <code className="text-crucible-accent-hover">{en.id}</code>
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          deleteEntity(en.id);
                          setSel(null);
                        }}
                        className="text-[11px] text-red-400 hover:text-red-300"
                      >
                        delete
                      </button>
                      <button
                        type="button"
                        onClick={() => setSel(null)}
                        className="text-[11px] text-crucible-muted hover:text-crucible-fg"
                      >
                        close
                      </button>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <label className="flex items-start justify-between gap-2 text-sm text-crucible-fg">
                      <span className="pt-1 text-crucible-muted">Content ref</span>
                      <span className="flex flex-col items-end gap-0.5">
                        <input
                          value={en.ref}
                          list="level-content-items"
                          onChange={(e) => setEntityField(en.id, 'ref', e.target.value)}
                          placeholder="pick a specific Content item"
                          className="w-40 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-sm text-crucible-fg"
                        />
                        <datalist id="level-content-items">
                          {[...(palette ?? [])].sort().map((name) => (
                            <option key={name} value={name} />
                          ))}
                        </datalist>
                        {palette && palette.size === 0 ? (
                          <span className="max-w-40 text-right text-[10px] text-crucible-muted">
                            No Content items yet — add the enemy/pickup in Content first, then pick
                            it here.
                          </span>
                        ) : en.ref && palette && !palette.has(en.ref) ? (
                          <span className="max-w-40 text-right text-[10px] text-amber-300">
                            ⚠ “{en.ref}” isn’t in Content — pick one from the list.
                          </span>
                        ) : null}
                      </span>
                    </label>
                    <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                      <span className="text-crucible-muted">Role</span>
                      <select
                        value={en.role ?? 'prop'}
                        onChange={(e) => setEntityField(en.id, 'role', e.target.value)}
                        className="w-40 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-sm text-crucible-fg"
                      >
                        {ENTITY_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                      <span className="text-crucible-muted">t (0–1 along track)</span>
                      <input
                        type="number"
                        step={0.01}
                        min={0}
                        max={1}
                        value={en.t}
                        onChange={(e) => setEntityField(en.id, 't', Number(e.target.value))}
                        className="w-24 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-right text-sm text-crucible-fg"
                      />
                    </label>
                    <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                      <span className="text-crucible-muted">Height above ground (m)</span>
                      <input
                        type="number"
                        step={0.5}
                        value={en.elevation ?? ''}
                        placeholder="on ground"
                        onChange={(e) =>
                          setEntityField(
                            en.id,
                            'elevation',
                            e.target.value === '' ? undefined : Number(e.target.value),
                          )
                        }
                        className="w-24 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-right text-sm text-crucible-fg"
                      />
                    </label>
                    {en.role === 'exit' && (
                      <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                        <span className="text-crucible-muted">Exit to (level)</span>
                        <input
                          value={en.exitTo ?? ''}
                          placeholder="level id / name"
                          onChange={(e) =>
                            setEntityField(en.id, 'exitTo', e.target.value || undefined)
                          }
                          className="w-40 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-sm text-crucible-fg"
                        />
                      </label>
                    )}
                    {(en.role === 'enemy' || en.role === 'hazard') && (
                      <>
                        <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                          <span className="text-crucible-muted">Hits to defeat</span>
                          <input
                            type="number"
                            step={1}
                            min={1}
                            value={en.hp ?? ''}
                            placeholder="1 (stomp)"
                            onChange={(e) =>
                              setEntityField(
                                en.id,
                                'hp',
                                e.target.value === '' ? undefined : Number(e.target.value),
                              )
                            }
                            className="w-24 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-right text-sm text-crucible-fg"
                          />
                        </label>
                        <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                          <span className="text-crucible-muted">Movement</span>
                          <select
                            value={en.movement ?? 'static'}
                            onChange={(e) => setEntityField(en.id, 'movement', e.target.value)}
                            className="w-32 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-sm text-crucible-fg"
                          >
                            {['static', 'patrol', 'chase'].map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </label>
                        {en.movement === 'patrol' && (
                          <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                            <span className="text-crucible-muted">Patrol range (m)</span>
                            <input
                              type="number"
                              step={0.5}
                              value={en.patrolRange ?? ''}
                              placeholder="span"
                              onChange={(e) =>
                                setEntityField(
                                  en.id,
                                  'patrolRange',
                                  e.target.value === '' ? undefined : Number(e.target.value),
                                )
                              }
                              className="w-24 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-right text-sm text-crucible-fg"
                            />
                          </label>
                        )}
                      </>
                    )}
                  </div>
                  <p className="mt-1 text-[11px] text-crucible-muted">
                    Drag the marker in the view to move it. Leave elevation blank to sit on the
                    track. Role &amp; behavior (HP / movement) drive the previs + guide the build.
                  </p>
                </div>
              );
            }
            if (sel.type === 'terrace') {
              const ter = (lvl.terraces ?? []).find((x) => x.id === sel.id);
              if (!ter) return null;
              const terField = (label: string, key: 'tStart' | 'tEnd' | 'height', step: number) => (
                <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                  <span className="text-crucible-muted">{label}</span>
                  <input
                    type="number"
                    step={step}
                    value={ter[key]}
                    onChange={(e) => setTerraceField(ter.id, key, Number(e.target.value))}
                    className="w-24 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-right text-sm text-crucible-fg"
                  />
                </label>
              );
              return (
                <div className="rounded-lg border border-[#c792ea]/40 bg-[#c792ea]/5 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-crucible-fg">
                      Terrace <code className="text-[#c792ea]">{ter.id}</code>
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          deleteTerrace(ter.id);
                          setSel(null);
                        }}
                        className="text-[11px] text-red-400 hover:text-red-300"
                      >
                        delete
                      </button>
                      <button
                        type="button"
                        onClick={() => setSel(null)}
                        className="text-[11px] text-crucible-muted hover:text-crucible-fg"
                      >
                        close
                      </button>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    {terField('t start (0–1)', 'tStart', 0.01)}
                    {terField('t end (0–1)', 'tEnd', 0.01)}
                    {terField('Height (m)', 'height', 0.5)}
                  </div>
                  <p className="mt-1 text-[11px] text-crucible-muted">
                    Overhead one-way platform — an alternate high route. Drag it in the view to move
                    + raise; height is reachability-checked against the jump apex.
                  </p>
                </div>
              );
            }
            const s = lvl.segments?.[sel.index];
            if (!s) return null;
            const numField = (label: string, key: string, val: number | undefined, step = 0.5) => (
              <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                <span className="text-crucible-muted">{label}</span>
                <input
                  type="number"
                  step={step}
                  value={val ?? 0}
                  onChange={(e) => setSegmentField(sel.index, key, Number(e.target.value))}
                  className="w-24 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-right text-sm text-crucible-fg"
                />
              </label>
            );
            return (
              <div className="rounded-lg border border-crucible-accent/40 bg-crucible-accent/5 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-crucible-fg">
                    Segment {sel.index}{' '}
                    <code className="text-crucible-muted">
                      {s.fromId} → {s.toId}
                    </code>
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => duplicateSegment(sel.index)}
                      className="text-[11px] text-crucible-muted hover:text-crucible-fg"
                    >
                      duplicate
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        deleteSegment(sel.index);
                        setSel(null);
                      }}
                      className="text-[11px] text-red-400 hover:text-red-300"
                    >
                      delete
                    </button>
                    <button
                      type="button"
                      onClick={() => setSel(null)}
                      className="text-[11px] text-crucible-muted hover:text-crucible-fg"
                    >
                      close
                    </button>
                  </div>
                </div>
                <div className="grid gap-2">
                  <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                    <span className="text-crucible-muted">Kind</span>
                    <select
                      value={s.kind}
                      onChange={(e) => setSegmentField(sel.index, 'kind', e.target.value)}
                      className="w-32 rounded border border-crucible-border bg-crucible-bg-elevated px-2 py-1 text-sm text-crucible-fg"
                    >
                      {SEGMENT_PALETTE.map((k) => (
                        <option key={k} value={k}>
                          {k}
                        </option>
                      ))}
                    </select>
                  </label>
                  {numField('Span (m)', 'span', s.span)}
                  {s.kind === 'fence' &&
                    numField('Obstacle height (m)', 'obstacleHeight', s.obstacleHeight)}
                  {s.kind === 'fence' &&
                    numField('Obstacle width (m)', 'obstacleWidth', s.obstacleWidth, 0.1)}
                  {s.kind === 'reversal' &&
                    numField('Clear window (s)', 'clearWindowS', s.clearWindowS)}
                  {s.kind === 'collapse' &&
                    numField('Collapse speed (m/s)', 'collapseSpeed', s.collapseSpeed, 0.1)}
                  <label className="flex items-center justify-between gap-2 text-sm text-crucible-fg">
                    <span className="text-crucible-muted">Requires simultaneous</span>
                    <input
                      type="checkbox"
                      checked={s.requiresSimultaneous ?? false}
                      onChange={(e) =>
                        setSegmentField(
                          sel.index,
                          'requiresSimultaneous',
                          e.target.checked || undefined,
                        )
                      }
                    />
                  </label>
                </div>
              </div>
            );
          })()
        : null}

      {/* JSON editor — the source of truth until the in-viewport gizmo lands */}
      {/* Open by default only for kinds without a rich editing surface. */}
      <details open={!isKindChoice(level.kind)}>
        <summary className="cursor-pointer text-[11px] uppercase tracking-wide text-crucible-muted">
          Level data (JSON)
        </summary>
        <textarea
          value={dataText}
          onChange={(e) => commitData(e.target.value)}
          spellCheck={false}
          className="mt-2 min-h-[220px] w-full rounded border border-crucible-border bg-crucible-bg-elevated p-2 font-mono text-xs text-crucible-fg"
        />
      </details>
      <p className="text-[11px] text-crucible-muted">
        Click a point or segment to edit its variables; drag a point to change elevation; use Add to
        append pieces. Validation runs live against the authored 3Cs (edit via the “3Cs profile”
        entry). Track view is Canvas 2D for now — the Three.js-orthographic swap is pending offline
        dep limits.
      </p>
    </div>
  );
}
